import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  serviceCreateSchema,
  type ServiceCreateInput,
  type Service,
} from '@inafana/shared';
import { api, ApiError } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';

const EMPTY: ServiceCreateInput = {
  slug: '',
  title: '',
  description: '',
  icon: null,
  order: 0,
  isPublished: true,
};

export function ServicesAdmin(): React.JSX.Element {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<Service | null>(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'services'],
    queryFn: () => api<{ services: Service[] }>('/admin/services'),
  });

  const form = useForm<ServiceCreateInput>({
    resolver: zodResolver(serviceCreateSchema),
    defaultValues: EMPTY,
  });

  const onOpen = (svc: Service | null): void => {
    setEditing(svc);
    form.reset(
      svc
        ? {
            slug: svc.slug,
            title: svc.title,
            description: svc.description,
            icon: svc.icon,
            order: svc.order,
            isPublished: svc.isPublished,
          }
        : EMPTY,
    );
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: (input: ServiceCreateInput) =>
      editing
        ? api<{ service: Service }>(`/admin/services/${editing.id}`, {
            method: 'PATCH',
            body: input,
          })
        : api<{ service: Service }>('/admin/services', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'services'] });
      setOpen(false);
      toast({ title: editing ? 'Service updated' : 'Service created' });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Save failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/services/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'services'] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Services</h1>
          <p className="text-sm text-muted-foreground">What you offer.</p>
        </div>
        <Button onClick={() => onOpen(null)}>
          <Plus className="mr-2 h-4 w-4" /> New service
        </Button>
      </header>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.services.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No services yet.
                </TableCell>
              </TableRow>
            )}
            {data?.services.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.title}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{s.slug}</TableCell>
                <TableCell>{s.order}</TableCell>
                <TableCell>
                  <Badge variant={s.isPublished ? 'default' : 'secondary'}>
                    {s.isPublished ? 'live' : 'hidden'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => onOpen(s)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Delete "${s.title}"?`)) remove.mutate(s.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit service' : 'New service'}</DialogTitle>
          </DialogHeader>
          <form
            id="service-form"
            onSubmit={form.handleSubmit((d) => save.mutate(d))}
            className="space-y-3"
          >
            <div className="space-y-1.5">
              <Label htmlFor="title">Title</Label>
              <Input id="title" {...form.register('title')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" {...form.register('slug')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" rows={4} {...form.register('description')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="icon">Icon</Label>
                <Input
                  id="icon"
                  placeholder="optional"
                  {...form.register('icon', {
                    setValueAs: (v: string) => (v === '' ? null : v),
                  })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="order">Order</Label>
                <Input id="order" type="number" {...form.register('order', { valueAsNumber: true })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="isPublished"
                checked={form.watch('isPublished')}
                onCheckedChange={(v) => form.setValue('isPublished', v)}
              />
              <Label htmlFor="isPublished">Published</Label>
            </div>
          </form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="service-form" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
